import { useCallback, useState } from "react";

import {
  disablePermissionProfilePreset,
  enablePermissionProfilePreset,
} from "@/app/access-presets/data_sources/services";
import type { APIPermissionProfilePreset } from "@/core/data_sources/models/APIPermissionProfilePreset";
import { AppStore } from "@/core/data_sources/store/app";
import { Button } from "@/core/ui/atoms";
import { Banner } from "@/core/ui/molecules";
import { ErrorResponse } from "@/core/utils/request";

export function DisableEnableDialog({
  onClose,
  onSaved,
  preset,
}: {
  readonly onClose: () => void;
  readonly onSaved: () => void;
  readonly preset: APIPermissionProfilePreset;
}) {
  const d = preset.getData();
  const isActive = d.status === "active";
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const handleToggle = useCallback(async () => {
    setErrors([]);
    setSaving(true);
    try {
      const resp = isActive
        ? await disablePermissionProfilePreset(d.id!).response
        : await enablePermissionProfilePreset(d.id!).response;
      if ((resp.data as ErrorResponse).error) throw resp.data;
      AppStore.instance.actions.notification(
        0,
        `Preset ${isActive ? "disabled" : "enabled"}`,
        2500,
        true,
      );
      onSaved();
      onClose();
    } catch (err) {
      const e = err as ErrorResponse;
      if (e.status === 409)
        setErrors([`Preset is already ${isActive ? "disabled" : "active"}.`]);
      else if (e.status === 400)
        setErrors(["Enable failed: capabilities are no longer valid."]);
      else setErrors([e.error?.message || "Action failed"]);
      setSaving(false);
    }
  }, [isActive, d.id, onSaved, onClose]);

  return (
    <div className="flex max-h-[calc(100vh-7.5rem)] w-[520px] max-w-[96vw] flex-col overflow-hidden rounded-dialog bg-surface shadow-dialog">
      <header className="border-b border-border px-5 py-5">
        <h2 className="text-lg font-extrabold text-ink">
          {isActive ? "Disable" : "Enable"} “{d.name}”?
        </h2>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
        {isActive ? (
          <Banner tone="warning">
            New tenants on plans using this preset will NOT receive it. Existing
            tenants keep their installed copies. Plan Engine will show
            validation warnings for affected plans.
          </Banner>
        ) : (
          <Banner tone="ok">
            Re-enabling re-runs full validation. If capabilities became invalid,
            the enable will be rejected.
          </Banner>
        )}
        {errors.length > 0 ? (
          <Banner tone="error">
            <ul className="ml-4 list-disc">
              {errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </Banner>
        ) : null}
      </div>
      <footer className="flex justify-end gap-2 border-t border-border bg-panel-muted px-5 py-3.5">
        <Button disabled={saving} onClick={onClose}>
          Cancel
        </Button>
        <Button
          disabled={saving}
          onClick={handleToggle}
          variant={isActive ? "danger" : "primary"}
        >
          {isActive ? "Disable preset" : "Enable preset"}
        </Button>
      </footer>
    </div>
  );
}
